import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast, showLargeToast } from '@/components/ui/sonner';
import { motion } from 'framer-motion';
import { API, useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import EventsListDialog from '@/components/EventsListDialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { User as UserIcon, Briefcase, Heart, MapPin, Users, Settings, UserPlus, UserCheck, UserMinus, Camera, Upload, X, MessageCircle, Eye, EyeOff, Crown, ChevronDown, Dumbbell, UtensilsCrossed, Compass, Palette, Flag, AlertTriangle, Lock, Plus, ImagePlus, Ban, ArrowLeft } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { INTEREST_CATEGORIES, CATEGORY_STYLES, groupInterestsByCategory } from '@/constants/interests';
import { ALL_COUNTRIES as COUNTRIES } from '@/constants/countries';
import { NATIONALITIES } from '@/constants';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useIsIOSApp } from '@/hooks/useIsIOSApp';
import BiometricSettings from '@/components/BiometricSettings';
import SearchableSelect from '@/components/SearchableSelect';
import GamificationCard from '@/components/GamificationCard';

const CATEGORY_LABELS = { jobs: 'Job', housing: 'Housing', marketplace: 'Marketplace', businesses: 'Business' };
const CATEGORY_ROUTES = { jobs: '/jobs', housing: '/housing', marketplace: '/marketplace', businesses: '/businesses' };
const CATEGORY_COLORS = { jobs: 'bg-blue-100 text-blue-700', housing: 'bg-green-100 text-green-700', marketplace: 'bg-orange-100 text-orange-700', businesses: 'bg-purple-100 text-purple-700' };
const STATUS_COLORS = { approved: 'bg-green-100 text-green-700', pending: 'bg-amber-100 text-amber-700', rejected: 'bg-red-100 text-red-700' };

const MyListingsTab = () => {
  const [listings, setListings] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchListings = async () => {
      try {
        const { data } = await axios.get(`${API}/users/me/listings`);
        setListings(data.listings || []);
        setCounts(data.counts || {});
      } catch (err) {
        console.error('Failed to fetch listings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchListings();
  }, []);

  const filtered = filter === 'all' ? listings : listings.filter(l => l.category === filter);

  return (
    <div className="bg-white rounded-3xl p-6 sm:p-8 border-2 border-border">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <h2 className="text-2xl font-heading font-bold">My Listings</h2>
        <span className="text-sm text-muted-foreground">{counts.total || 0} total listing{counts.total !== 1 ? 's' : ''}</span>
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { key: 'all', label: 'All', count: counts.total },
          { key: 'jobs', label: 'Jobs', count: counts.jobs },
          { key: 'housing', label: 'Housing', count: counts.housing },
          { key: 'marketplace', label: 'Marketplace', count: counts.marketplace },
          { key: 'businesses', label: 'Businesses', count: counts.businesses },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${filter === f.key ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            data-testid={`filter-${f.key}`}
          >
            {f.label} ({f.count || 0})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12" data-testid="no-listings">
          <Briefcase className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p className="text-muted-foreground">No listings found</p>
          <p className="text-sm text-muted-foreground mt-1">Post a job, housing, item, or business from the Directory tab</p>
        </div>
      ) : (
        <div className="divide-y">
          {filtered.map(listing => (
            <Link
              key={`${listing.category}-${listing.listing_id}`}
              to={`${CATEGORY_ROUTES[listing.category]}/${listing.listing_id}`}
              className="flex items-center gap-4 py-4 hover:bg-gray-50 rounded-xl px-3 -mx-3 transition-colors"
              data-testid={`listing-${listing.listing_id}`}
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{listing.title}</p>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[listing.category]}`}>
                    {CATEGORY_LABELS[listing.category]}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[listing.moderation_status] || 'bg-gray-100 text-gray-600'}`}>
                    {listing.moderation_status || 'pending'}
                  </span>
                  {listing.city && <span className="text-xs text-muted-foreground">{listing.city}</span>}
                  {listing.price && <span className="text-xs text-muted-foreground">${listing.price}</span>}
                </div>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-400 -rotate-90 flex-shrink-0" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const EnhancedProfile = () => {
  const { user } = useAuth();
  const isIOSApp = useIsIOSApp();
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profileData, setProfileData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editForm, setEditForm] = useState({});
  const [openCategories, setOpenCategories] = useState([]);
  const [newWork, setNewWork] = useState({ company: '', position: '', location: '', start_date: '', end_date: '', description: '' });
  const [connections, setConnections] = useState({ contacts: [], pending_requests: [], sent_requests: [] });
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [showPhotoMenu, setShowPhotoMenu] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);
  const [dismissingUser, setDismissingUser] = useState(false);
  const [dismissedUsers, setDismissedUsers] = useState([]);
  const [loadingDismissed, setLoadingDismissed] = useState(false);
  const [countrySearchFilter, setCountrySearchFilter] = useState('');
  const countryListRef = useRef(null);

  // Events list dialog (opened by clicking Hosted/Attended stat cards)
  const [eventsListDialog, setEventsListDialog] = useState({ open: false, type: null });
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Email change state
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [emailChangeForm, setEmailChangeForm] = useState({ newEmail: '', password: '' });
  const [changingEmail, setChangingEmail] = useState(false);
  
  // Get active tab from URL query parameter (for notification bell navigation)
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'overview');
  
  // Update activeTab when URL changes
  useEffect(() => {
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  // Sync tab changes back to URL so browser back button preserves the active tab
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    if (newTab === 'overview') {
      searchParams.delete('tab');
    } else {
      searchParams.set('tab', newTab);
    }
    setSearchParams(searchParams, { replace: true });
  };
  
  const isOwnProfile = !userId || userId === user?.user_id;
  const viewUserId = userId || user?.user_id;

  useEffect(() => {
    if (user) {
      fetchProfile();
      if (isOwnProfile) {
        fetchConnections();
        fetchDismissedUsers();
      } else {
        // Track profile view when viewing someone else's profile
        trackProfileView();
      }
    }
  }, [user, userId]);

  const trackProfileView = async () => {
    if (!userId || userId === user?.user_id) return;
    try {
      await axios.post(`${API}/profile-views/${userId}`);
    } catch (error) {
      // Silent fail for tracking
    }
  };

  const fetchProfile = async () => {
    try {
      const endpoint = isOwnProfile ? '/users/me' : `/users/${viewUserId}/profile`;
      const response = await axios.get(`${API}${endpoint}`);
      let data = response.data;
      // /users/me doesn't include hosted_count/attended_count — fetch them separately
      if (isOwnProfile) {
        try {
          const statsRes = await axios.get(`${API}/users/me/activity-stats`);
          data = { ...data, ...statsRes.data };
        } catch {}
      }
      setProfileData(data);
      setEditForm(data);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchConnections = async () => {
    try {
      const response = await axios.get(`${API}/connections`);
      setConnections(response.data);
    } catch (error) {
      console.error('Failed to load connections');
    }
  };

  const fetchDismissedUsers = async () => {
    setLoadingDismissed(true);
    try {
      // Get the list of dismissed user IDs
      const dismissedRes = await axios.get(`${API}/users/dismissed`);
      const dismissedIds = dismissedRes.data.dismissed_users || [];
      
      if (dismissedIds.length === 0) {
        setDismissedUsers([]);
        return;
      }
      
      // Fetch details for each dismissed user
      const userDetails = await Promise.all(
        dismissedIds.map(async (userId) => {
          try {
            const res = await axios.get(`${API}/users/${userId}/profile`);
            return res.data;
          } catch {
            return { user_id: userId, name: 'Unknown User', deleted: true };
          }
        })
      );
      
      setDismissedUsers(userDetails);
    } catch (error) {
      console.error('Failed to load dismissed users');
    } finally {
      setLoadingDismissed(false);
    }
  };

  const handleRestoreUser = async (userId) => {
    try {
      await axios.delete(`${API}/users/${userId}/dismiss`);
      toast.success('User restored to recommendations');
      // Refresh the dismissed users list
      fetchDismissedUsers();
    } catch (error) {
      toast.error('Failed to restore user');
    }
  };

  const handleSaveProfile = async () => {
    try {
      await axios.put(`${API}/users/me/profile`, editForm);
      toast.success('Profile updated successfully');
      setIsEditing(false);
      fetchProfile();
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleToggleInterest = (interest) => {
    const currentInterests = editForm.interests || [];
    if (currentInterests.includes(interest)) {
      setEditForm({ ...editForm, interests: currentInterests.filter(i => i !== interest) });
    } else {
      setEditForm({ ...editForm, interests: [...currentInterests, interest] });
    }
  };

  const handleToggleCountry = (country) => {
    const currentCountries = editForm.countries_visited || [];
    if (currentCountries.includes(country)) {
      setEditForm({ ...editForm, countries_visited: currentCountries.filter(c => c !== country) });
    } else {
      setEditForm({ ...editForm, countries_visited: [...currentCountries, country] });
    }
  };

  const handleJumpToLetter = (letter) => {
    setCountrySearchFilter(letter);
    // Scroll to the first country starting with this letter
    if (countryListRef.current) {
      const firstMatch = COUNTRIES.find(c => c.toUpperCase().startsWith(letter));
      if (firstMatch) {
        const element = document.getElementById(`country-${firstMatch.replace(/[^a-zA-Z0-9]/g, '-')}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }
  };

  // Get unique starting letters from countries list
  const countryLetters = [...new Set(COUNTRIES.map(c => c[0].toUpperCase()))].sort();

  // Filter countries based on search
  const filteredCountries = countrySearchFilter 
    ? COUNTRIES.filter(c => c.toLowerCase().includes(countrySearchFilter.toLowerCase()))
    : COUNTRIES;

  const handleAddWork = () => {
    if (newWork.company && newWork.position) {
      const updated = [...(editForm.work_experience || []), { ...newWork }];
      setEditForm({ ...editForm, work_experience: updated });
      setNewWork({ company: '', position: '', location: '', start_date: '', end_date: '', description: '' });
    }
  };

  const handleRemoveWork = (index) => {
    const updated = editForm.work_experience.filter((_, i) => i !== index);
    setEditForm({ ...editForm, work_experience: updated });
  };

  const handleProfilePictureChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    setUploadingPicture(true);
    try {
      // Auto-compress: resize to max 800px and convert to JPEG
      const compressed = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          const maxDim = 800;
          if (width > maxDim || height > maxDim) {
            if (width > height) {
              height = Math.round(height * (maxDim / width));
              width = maxDim;
            } else {
              width = Math.round(width * (maxDim / height));
              height = maxDim;
            }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', 0.8));
        };
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
      });

      try {
        await axios.post(`${API}/users/me/profile-picture`, {
          image_data: compressed
        });
        
        setProfileData({ ...profileData, profile_picture: compressed });
        setEditForm({ ...editForm, profile_picture: compressed });
        showLargeToast('Profile picture updated!', 'Your new photo is now visible to other members.');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to upload profile picture');
      } finally {
        setUploadingPicture(false);
      }
    } catch (error) {
      toast.error('Failed to process image');
      setUploadingPicture(false);
    }
  };

  const handleRemoveProfilePicture = async () => {
    setUploadingPicture(true);
    try {
      await axios.put(`${API}/users/me/profile`, { profile_picture: '' });
      setProfileData({ ...profileData, profile_picture: '' });
      setEditForm({ ...editForm, profile_picture: '' });
      toast.success('Profile picture removed');
    } catch (error) {
      toast.error('Failed to remove profile picture');
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleSendConnection = async () => {
    try {
      await axios.post(`${API}/connections/request`, { target_user_id: viewUserId });
      toast.success('Connection request sent');
      fetchProfile();
      fetchConnections();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    }
  };

  const handleAcceptConnection = async (userId) => {
    try {
      await axios.post(`${API}/connections/accept/${userId}`);
      toast.success('Connection accepted');
      fetchConnections();
    } catch (error) {
      toast.error('Failed to accept connection');
    }
  };

  const handleRejectConnection = async (userId) => {
    try {
      await axios.post(`${API}/connections/reject/${userId}`);
      toast.success('Connection rejected');
      fetchConnections();
    } catch (error) {
      toast.error('Failed to reject connection');
    }
  };

  const handleReportUser = async () => {
    if (!reportReason) {
      toast.error('Please select a reason for reporting');
      return;
    }
    
    setSubmittingReport(true);
    try {
      await axios.post(`${API}/moderation/report`, {
        reported_id: viewUserId,
        content_type: 'user',
        reason: reportReason,
        description: reportDescription || ''
      });
      toast.success('Report submitted. Our team will review it within 24 hours.');
      setShowReportDialog(false);
      setReportReason('');
      setReportDescription('');
    } catch (error) {
      if (error.response?.data?.detail === 'You have already reported this content') {
        toast.error('You have already reported this user');
      } else {
        toast.error('Failed to submit report. Please try again.');
      }
    } finally {
      setSubmittingReport(false);
    }
  };

  const handleBlockUser = async () => {
    if (!window.confirm('Block this user? You will no longer see their content.')) return;
    try {
      await axios.post(`${API}/moderation/block`, { blocked_user_id: viewUserId });
      toast.success('User blocked');
      navigate('/find-expats');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block user');
    }
  };

  const handleDismissUser = async () => {
    setDismissingUser(true);
    try {
      await axios.post(`${API}/users/${viewUserId}/dismiss`);
      toast.success('User removed from recommendations', {
        description: 'They won\'t appear in "Expats to Connect With" anymore.'
      });
      // Navigate back to dashboard or previous page
      navigate(-1);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response?.data?.detail || 'Cannot dismiss this user');
      } else {
        toast.error('Failed to dismiss user. Please try again.');
      }
    } finally {
      setDismissingUser(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordForm.currentPassword === passwordForm.newPassword) {
      toast.error('New password must be different from current password');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });
      toast.success('Password changed successfully!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    if (!emailChangeForm.newEmail || !emailChangeForm.password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    // Show warning toast first
    toast('Are you sure you want to change your email?', {
      description: 'You will need to verify your new email address before the change takes effect. Your current email will remain active until verification is complete.',
      duration: 10000,
      action: {
        label: 'Confirm',
        onClick: () => submitEmailChange(),
      },
      cancel: {
        label: 'Cancel',
      },
    });
  };

  const submitEmailChange = async () => {
    setChangingEmail(true);
    try {
      const { data } = await axios.post(`${API}/auth/change-email`, {
        new_email: emailChangeForm.newEmail,
        password: emailChangeForm.password,
      });
      toast.success(data.message, { duration: 8000 });
      setEmailChangeForm({ newEmail: '', password: '' });
      setShowEmailChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request email change');
    } finally {
      setChangingEmail(false);
    }
  };

  const isContact = profileData && user && profileData.contacts?.includes(user.user_id);
  const hasPendingRequest = connections.sent_requests.some(r => r.user_id === viewUserId);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <div className="text-lg">Loading profile...</div>
        </div>
      </div>
    );
  }

  if (!profileData) return null;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Back button — visible only when viewing another user's profile.
              Uses location.state.label set by the navigating page (Expats list,
              Event attendees) for a context-aware label; falls back to "Back". */}
          {!isOwnProfile && (
            <button
              type="button"
              onClick={() => {
                if (window.history.length > 1) navigate(-1);
                else navigate('/find-expats');
              }}
              className="inline-flex items-center gap-1.5 mb-4 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              data-testid="profile-back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              {`Back to ${location.state?.label || 'Expats'}`}
            </button>
          )}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Profile Header */}
            <div className="bg-white rounded-3xl p-8 md:p-12 border-2 border-border brutal-shadow mb-8">
              <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="flex-shrink-0 relative">
                  {profileData.profile_picture ? (
                    <img
                      src={profileData.profile_picture}
                      alt={profileData.name}
                      className="w-32 h-32 rounded-full border-4 border-border object-cover"
                      data-testid="profile-picture"
                    />
                  ) : (
                    <div className="w-32 h-32 bg-primary rounded-full flex items-center justify-center text-5xl font-heading font-bold text-primary-foreground border-4 border-border">
                      {profileData.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  
                  {/* Upload/Change Picture Button - Only shown for own profile */}
                  {isOwnProfile && (
                    <div className="absolute -bottom-2 -right-2 flex gap-1">
                      {/* Hidden file input for gallery */}
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={(e) => { setShowPhotoMenu(false); handleProfilePictureChange(e); }}
                        accept={isIOSApp ? ".jpg,.jpeg,.png,.gif,.webp,.heic" : "image/*"}
                        className="hidden"
                        data-testid="profile-picture-input"
                      />
                      {/* Hidden file input for camera selfie - hidden on iOS to avoid camera permission */}
                      {!isIOSApp && (
                        <input
                          type="file"
                          ref={cameraInputRef}
                          onChange={(e) => { setShowPhotoMenu(false); handleProfilePictureChange(e); }}
                          accept="image/*"
                          capture="user"
                          className="hidden"
                          data-testid="profile-camera-input"
                        />
                      )}

                      {/* + Button */}
                      <button
                        type="button"
                        onClick={() => {
                          if (isIOSApp) {
                            // On iOS, skip the menu and go directly to gallery
                            fileInputRef.current?.click();
                          } else {
                            setShowPhotoMenu(!showPhotoMenu);
                          }
                        }}
                        disabled={uploadingPicture}
                        className={`rounded-full w-10 h-10 p-0 bg-accent hover:bg-accent/90 border-2 border-white shadow-lg flex items-center justify-center cursor-pointer z-10 ${uploadingPicture ? 'pointer-events-none opacity-50' : ''}`}
                        data-testid="upload-picture-button"
                        title="Change photo"
                      >
                        {uploadingPicture ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Plus className="w-5 h-5 text-white" />
                        )}
                      </button>

                      {/* Photo option menu */}
                      {showPhotoMenu && (
                        <>
                          <div className="fixed inset-0 z-20" onClick={() => setShowPhotoMenu(false)} />
                          <div className="absolute bottom-12 right-0 z-30 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden w-48 animate-in fade-in slide-in-from-bottom-2 duration-200" data-testid="photo-menu">
                            <button
                              type="button"
                              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-violet-50 transition-colors"
                              onClick={() => { cameraInputRef.current?.click(); }}
                              data-testid="take-selfie-btn"
                            >
                              <Camera className="w-4 h-4 text-violet-500" />
                              Take a selfie
                            </button>
                            <div className="border-t border-gray-100" />
                            <button
                              type="button"
                              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-violet-50 transition-colors"
                              onClick={() => { fileInputRef.current?.click(); }}
                              data-testid="choose-gallery-btn"
                            >
                              <ImagePlus className="w-4 h-4 text-violet-500" />
                              Choose from gallery
                            </button>
                          </div>
                        </>
                      )}

                      {profileData.profile_picture && (
                        <Button
                          onClick={handleRemoveProfilePicture}
                          disabled={uploadingPicture}
                          size="sm"
                          variant="destructive"
                          className="rounded-full w-10 h-10 p-0 border-2 border-white shadow-lg"
                          data-testid="remove-picture-button"
                          title="Remove picture"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                    <div>
                      <h1 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold mb-2">{profileData.name}</h1>
                      <div className="flex flex-wrap gap-2 text-muted-foreground text-sm sm:text-base">
                        <span className="flex items-center">
                          <MapPin className="w-4 h-4 mr-1" />
                          {profileData.residence_city}, {profileData.residence_country}
                        </span>
                        {profileData.nationality && (
                          <span>• {profileData.nationality}</span>
                        )}
                      </div>
                    </div>
                    
                    {isOwnProfile ? (
                      <div className="flex flex-wrap gap-2">
                        <Link to="/profile-viewers">
                          <Button
                            variant="outline"
                            className="rounded-full border-2 text-sm"
                            size="sm"
                          >
                            <Eye className="w-4 h-4 mr-1 sm:mr-2" />
                            <span className="hidden sm:inline">Profile Views</span>
                            <span className="sm:hidden">Profile Views</span>
                          </Button>
                        </Link>
                        <Button
                          onClick={() => setIsEditing(!isEditing)}
                          variant="outline"
                          className="rounded-full border-2 text-sm"
                          size="sm"
                          data-testid="edit-profile-button"
                        >
                          {isEditing ? 'Cancel' : 'Edit Profile'}
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {isContact ? (
                          <>
                            <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-green-50 border border-green-200 text-green-700 text-sm font-medium">
                              <UserCheck className="w-3.5 h-3.5" />
                              Connected
                            </span>
                            <Link to={`/messages/${viewUserId}`}>
                              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full" size="sm">
                                <MessageCircle className="w-4 h-4 mr-1 sm:mr-2" />
                                Message
                              </Button>
                            </Link>
                          </>
                        ) : hasPendingRequest ? (
                          <Button disabled variant="outline" className="rounded-full" size="sm">
                            <UserCheck className="w-4 h-4 mr-1 sm:mr-2" />
                            Request Sent
                          </Button>
                        ) : (
                          <>
                            <Button
                              onClick={handleSendConnection}
                              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full brutal-shadow"
                              size="sm"
                            >
                              <UserPlus className="w-4 h-4 mr-1 sm:mr-2" />
                              Connect
                            </Button>
                            <Link to={`/messages/${viewUserId}`}>
                              <Button variant="outline" className="rounded-full" size="sm">
                                <MessageCircle className="w-4 h-4 mr-1 sm:mr-2" />
                                Message
                              </Button>
                            </Link>
                          </>
                        )}
                        {/* Report User Button */}
                        <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
                          <DialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-muted-foreground hover:text-destructive rounded-full"
                              data-testid="report-user-button"
                            >
                              <Flag className="w-4 h-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="sm:max-w-md">
                            <DialogHeader>
                              <DialogTitle className="flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5 text-destructive" />
                                Report User
                              </DialogTitle>
                              <DialogDescription>
                                Report this user for violating community guidelines. Our moderation team will review your report.
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                              <div className="space-y-2">
                                <Label htmlFor="report-reason">Reason for reporting *</Label>
                                <Select value={reportReason} onValueChange={setReportReason}>
                                  <SelectTrigger id="report-reason" data-testid="report-reason-select">
                                    <SelectValue placeholder="Select a reason" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="spam">Spam or fake account</SelectItem>
                                    <SelectItem value="harassment">Harassment or bullying</SelectItem>
                                    <SelectItem value="inappropriate">Inappropriate content</SelectItem>
                                    <SelectItem value="misleading">Misleading information</SelectItem>
                                    <SelectItem value="scam">Scam or fraud</SelectItem>
                                    <SelectItem value="impersonation">Impersonation</SelectItem>
                                    <SelectItem value="other">Other</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="report-description">Additional details (optional)</Label>
                                <Textarea
                                  id="report-description"
                                  value={reportDescription}
                                  onChange={(e) => setReportDescription(e.target.value)}
                                  placeholder="Please provide any additional context..."
                                  className="min-h-[100px]"
                                  data-testid="report-description-input"
                                />
                              </div>
                            </div>
                            <DialogFooter className="flex-col sm:flex-row gap-2">
                              <Button
                                variant="outline"
                                onClick={() => setShowReportDialog(false)}
                                className="w-full sm:w-auto"
                              >
                                Cancel
                              </Button>
                              <Button
                                onClick={handleReportUser}
                                disabled={!reportReason || submittingReport}
                                className="w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                data-testid="submit-report-button"
                              >
                                {submittingReport ? 'Submitting...' : 'Submit Report'}
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                        {/* Block User Button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-destructive rounded-full"
                          onClick={handleBlockUser}
                          data-testid="block-user-button"
                        >
                          <Ban className="w-4 h-4" />
                        </Button>
                        {/* Not Interested Button */}
                        <Button
                          onClick={handleDismissUser}
                          disabled={dismissingUser}
                          variant="outline"
                          size="sm"
                          className="text-muted-foreground hover:text-orange-600 hover:border-orange-600 rounded-full border-2"
                          data-testid="dismiss-user-button"
                        >
                          {dismissingUser ? (
                            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-1 sm:mr-2" />
                          ) : (
                            <EyeOff className="w-4 h-4 mr-1 sm:mr-2" />
                          )}
                          <span className="hidden sm:inline">Not Interested</span>
                          <span className="sm:hidden">Hide</span>
                        </Button>
                      </div>
                    )}
                  </div>

                  {profileData.about_me && (
                    <div className="mb-4">
                      <h3 className="font-semibold text-lg mb-2">About Me</h3>
                      <p className="text-muted-foreground">{profileData.about_me}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                    {isOwnProfile ? (
                      <button
                        type="button"
                        onClick={() => navigate('/connections')}
                        className="text-center p-4 bg-background rounded-xl hover:bg-primary/5 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary"
                        data-testid="profile-stat-connections"
                      >
                        <div className="text-2xl font-bold">{profileData.contacts?.length || 0}</div>
                        <div className="text-sm text-muted-foreground">Connections</div>
                      </button>
                    ) : (
                      <div className="text-center p-4 bg-background rounded-xl" data-testid="profile-stat-connections">
                        <div className="text-2xl font-bold">{profileData.contacts?.length || 0}</div>
                        <div className="text-sm text-muted-foreground">Connections</div>
                      </div>
                    )}
                    <div className="text-center p-4 bg-background rounded-xl" data-testid="profile-stat-countries">
                      <div className="text-2xl font-bold">{profileData.countries_visited?.length || 0}</div>
                      <div className="text-sm text-muted-foreground">Countries</div>
                    </div>
                    <div className="text-center p-4 bg-background rounded-xl" data-testid="profile-stat-communities">
                      <div className="text-2xl font-bold">{profileData.joined_communities?.length || 0}</div>
                      <div className="text-sm text-muted-foreground">Communities</div>
                    </div>
                    <div className="text-center p-4 bg-background rounded-xl" data-testid="profile-stat-interests">
                      <div className="text-2xl font-bold">{profileData.interests?.length || 0}</div>
                      <div className="text-sm text-muted-foreground">Interests</div>
                    </div>
                    {/* Activity row — hosted is clickable (public); attended is clickable for self/mutual connections only */}
                    <button
                      type="button"
                      onClick={() => setEventsListDialog({ open: true, type: 'hosted' })}
                      className="text-center p-4 bg-background rounded-xl hover:bg-primary/5 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary"
                      data-testid="profile-stat-hosted"
                    >
                      <div className="text-2xl font-bold">{profileData.hosted_count || 0}</div>
                      <div className="text-sm text-muted-foreground">Hosted</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setEventsListDialog({ open: true, type: 'attended' })}
                      className="text-center p-4 bg-background rounded-xl hover:bg-primary/5 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary"
                      data-testid="profile-stat-attended"
                    >
                      <div className="text-2xl font-bold">{profileData.attended_count || 0}</div>
                      <div className="text-sm text-muted-foreground">Attended</div>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Edit Profile Modal/Section */}
            {isEditing && isOwnProfile && (
              <div className="bg-white rounded-3xl p-8 border-2 border-border brutal-shadow mb-8">
                <h2 className="text-2xl font-heading font-bold mb-6">Edit Profile</h2>
                <div className="space-y-6">
                  <div className="p-4 bg-accent/10 rounded-xl border border-accent/20">
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Camera className="w-4 h-4" />
                      {isIOSApp 
                        ? 'To change your profile picture, tap the + button on your profile photo above'
                        : 'To change your profile picture, click the camera icon on your profile photo above'}
                    </p>
                  </div>
                  
                  {/* Basic Info Section */}
                  <div className="space-y-4">
                    <Label className="text-lg font-semibold">Basic Information</Label>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="edit_nationality">Nationality</Label>
                        <SearchableSelect
                          options={NATIONALITIES}
                          value={editForm.nationality || ''}
                          onValueChange={(value) => setEditForm({ ...editForm, nationality: value })}
                          placeholder="Select nationality"
                          searchPlaceholder="Type to search..."
                          data-testid="edit-nationality"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="edit_birth_country">Birth Country</Label>
                        <SearchableSelect
                          options={COUNTRIES}
                          value={editForm.birth_country || ''}
                          onValueChange={(value) => setEditForm({ ...editForm, birth_country: value })}
                          placeholder="Select country"
                          searchPlaceholder="Type to search..."
                          data-testid="edit-birth-country"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="edit_birth_city">Birth City</Label>
                        <Input
                          id="edit_birth_city"
                          value={editForm.birth_city || ''}
                          onChange={(e) => setEditForm({ ...editForm, birth_city: e.target.value })}
                          className="rounded-xl border-2"
                          placeholder="Enter city"
                          data-testid="edit-birth-city"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="edit_residence_country">Current Country</Label>
                        <SearchableSelect
                          options={COUNTRIES}
                          value={editForm.residence_country || ''}
                          onValueChange={(value) => setEditForm({ ...editForm, residence_country: value })}
                          placeholder="Select country"
                          searchPlaceholder="Type to search..."
                          data-testid="edit-residence-country"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="edit_residence_city">Current City</Label>
                        <Input
                          id="edit_residence_city"
                          value={editForm.residence_city || ''}
                          onChange={(e) => setEditForm({ ...editForm, residence_city: e.target.value })}
                          className="rounded-xl border-2"
                          placeholder="Enter city"
                          data-testid="edit-residence-city"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="edit_about">About Me</Label>
                    <Textarea
                      id="edit_about"
                      value={editForm.about_me || ''}
                      onChange={(e) => setEditForm({ ...editForm, about_me: e.target.value })}
                      className="rounded-xl border-2 min-h-[100px]"
                      data-testid="edit-about-me"
                    />
                  </div>
                  
                  {/* Interests Section - Categorized */}
                  <div className="space-y-4">
                    <Label className="text-lg font-semibold">Interests</Label>
                    <p className="text-sm text-muted-foreground">Select your interests from the categories below</p>
                    
                    <Accordion type="multiple" className="w-full space-y-3">
                      {Object.entries(INTEREST_CATEGORIES).map(([category, interests]) => {
                        const style = CATEGORY_STYLES[category];
                        const selectedCount = (editForm.interests || []).filter(i => interests.includes(i)).length;
                        
                        return (
                          <AccordionItem 
                            key={category} 
                            value={category}
                            className="border-0 rounded-xl overflow-hidden"
                          >
                            <AccordionTrigger 
                              className={`px-4 py-3 bg-gradient-to-r ${style.bgColor} text-white hover:no-underline rounded-xl data-[state=open]:rounded-b-none`}
                            >
                              <div className="flex items-center gap-3">
                                {category === "Business & Beyond" && <Briefcase className="w-5 h-5" />}
                                {category === "Fit & Fun" && <Dumbbell className="w-5 h-5" />}
                                {category === "Bites & Bright Lights" && <UtensilsCrossed className="w-5 h-5" />}
                                {category === "More to Explore" && <Compass className="w-5 h-5" />}
                                {category === "Arts & Culture" && <Palette className="w-5 h-5" />}
                                <span className="font-semibold">{category}</span>
                                {selectedCount > 0 && (
                                  <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                                    {selectedCount} selected
                                  </span>
                                )}
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="bg-gray-50 px-4 py-4 rounded-b-xl border-x border-b border-gray-200">
                              <div className="flex flex-wrap gap-2">
                                {interests.map((interest) => {
                                  const isSelected = (editForm.interests || []).includes(interest);
                                  return (
                                    <button
                                      key={interest}
                                      type="button"
                                      onClick={() => handleToggleInterest(interest)}
                                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all duration-200 ${
                                        isSelected 
                                          ? style.selectedColor + ' shadow-sm' 
                                          : style.tagColor + ' hover:shadow-sm'
                                      }`}
                                      data-testid={`interest-tag-${interest.toLowerCase().replace(/\s+/g, '-')}`}
                                    >
                                      {interest}
                                    </button>
                                  );
                                })}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        );
                      })}
                    </Accordion>
                    
                    {/* Selected interests summary */}
                    {(editForm.interests || []).length > 0 && (
                      <div className="mt-4 p-3 bg-primary/5 rounded-xl border border-primary/20">
                        <p className="text-sm font-medium text-primary mb-2">
                          Selected Interests ({(editForm.interests || []).length})
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {(editForm.interests || []).map((interest) => (
                            <span 
                              key={interest}
                              className="px-2 py-1 bg-primary text-primary-foreground rounded-full text-xs font-medium flex items-center gap-1"
                            >
                              {interest}
                              <button 
                                onClick={() => handleToggleInterest(interest)}
                                className="hover:bg-white/20 rounded-full p-0.5"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Countries Visited Section */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-semibold">Countries Visited</Label>
                      <span className="text-sm text-muted-foreground">
                        {editForm.countries_visited?.length || 0} selected
                      </span>
                    </div>
                    
                    {/* Search and Jump-to-Letter */}
                    <div className="space-y-2">
                      <Input
                        value={countrySearchFilter}
                        onChange={(e) => setCountrySearchFilter(e.target.value)}
                        placeholder="Search countries..."
                        className="rounded-xl border-2"
                        data-testid="country-search-input"
                      />
                      <div className="flex flex-wrap gap-1">
                        {countryLetters.map((letter) => (
                          <Button
                            key={letter}
                            variant={countrySearchFilter.toUpperCase() === letter ? "default" : "outline"}
                            size="sm"
                            className="w-8 h-8 p-0 text-xs rounded-lg"
                            onClick={() => {
                              if (countrySearchFilter.toUpperCase() === letter) {
                                setCountrySearchFilter('');
                              } else {
                                handleJumpToLetter(letter);
                              }
                            }}
                            data-testid={`letter-jump-${letter}`}
                          >
                            {letter}
                          </Button>
                        ))}
                        {countrySearchFilter && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs"
                            onClick={() => setCountrySearchFilter('')}
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Selected Countries Pills */}
                    {editForm.countries_visited?.length > 0 && (
                      <div className="flex flex-wrap gap-2 p-3 bg-muted/50 rounded-xl">
                        {editForm.countries_visited.map((country) => (
                          <span 
                            key={country} 
                            className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium flex items-center gap-2"
                          >
                            {country}
                            <button
                              onClick={() => handleToggleCountry(country)}
                              className="hover:text-destructive"
                              data-testid={`remove-country-${country.replace(/[^a-zA-Z0-9]/g, '-')}`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Country Checkboxes List */}
                    <ScrollArea className="h-[300px] rounded-xl border-2 p-3" ref={countryListRef}>
                      <div className="space-y-1">
                        {filteredCountries.length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-4">
                            No countries match your search
                          </p>
                        ) : (
                          filteredCountries.map((country) => (
                            <div 
                              key={country}
                              id={`country-${country.replace(/[^a-zA-Z0-9]/g, '-')}`}
                              className="flex items-center space-x-3 py-2 px-2 rounded-lg hover:bg-muted/50 cursor-pointer"
                              onClick={() => handleToggleCountry(country)}
                              data-testid={`country-checkbox-${country.replace(/[^a-zA-Z0-9]/g, '-')}`}
                            >
                              <Checkbox
                                id={`cb-${country}`}
                                checked={editForm.countries_visited?.includes(country) || false}
                                onCheckedChange={() => handleToggleCountry(country)}
                                className="pointer-events-none"
                              />
                              <label 
                                htmlFor={`cb-${country}`}
                                className="text-sm font-medium cursor-pointer flex-1"
                              >
                                {country}
                              </label>
                            </div>
                          ))
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                  
                  {/* Work Experience Section */}
                  <div className="space-y-2 border-t pt-6">
                    <Label>Work Experience</Label>
                    <div className="space-y-3 mb-3">
                      {editForm.work_experience?.map((work, index) => (
                        <div key={index} className="p-3 bg-muted rounded-xl flex justify-between items-start">
                          <div>
                            <p className="font-semibold">{work.position}</p>
                            <p className="text-sm text-muted-foreground">{work.company} • {work.location}</p>
                            <p className="text-xs text-muted-foreground">{work.start_date} - {work.end_date || 'Present'}</p>
                          </div>
                          <button
                            onClick={() => handleRemoveWork(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        value={newWork.position}
                        onChange={(e) => setNewWork({ ...newWork, position: e.target.value })}
                        placeholder="Job Title *"
                        className="rounded-xl border-2"
                      />
                      <Input
                        value={newWork.company}
                        onChange={(e) => setNewWork({ ...newWork, company: e.target.value })}
                        placeholder="Company *"
                        className="rounded-xl border-2"
                      />
                      <Input
                        value={newWork.location}
                        onChange={(e) => setNewWork({ ...newWork, location: e.target.value })}
                        placeholder="Location"
                        className="rounded-xl border-2"
                      />
                      <Input
                        value={newWork.start_date}
                        onChange={(e) => setNewWork({ ...newWork, start_date: e.target.value })}
                        placeholder="Start Date (e.g., Jan 2020)"
                        className="rounded-xl border-2"
                      />
                      <Input
                        value={newWork.end_date}
                        onChange={(e) => setNewWork({ ...newWork, end_date: e.target.value })}
                        placeholder="End Date (or leave blank)"
                        className="rounded-xl border-2"
                      />
                      <Button
                        onClick={handleAddWork}
                        variant="outline"
                        className="rounded-xl border-2"
                        type="button"
                        disabled={!newWork.position || !newWork.company}
                      >
                        Add Experience
                      </Button>
                    </div>
                  </div>
                  
                  <Button
                    onClick={handleSaveProfile}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 rounded-full font-bold brutal-shadow"
                    data-testid="save-profile-button"
                  >
                    Save Changes
                  </Button>
                </div>
              </div>
            )}

            {/* Gamification — Tier, Badges & Progress */}
            <div className="mb-6">
              <GamificationCard userId={isOwnProfile ? undefined : profileData?.user_id} />
            </div>

            {/* Profile Tabs */}
            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
              <div className="mb-8 overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
                <TabsList className="bg-white border-2 border-border p-1 h-12 sm:h-14 rounded-full inline-flex min-w-max">
                  <TabsTrigger value="overview" className="rounded-full px-4 sm:px-6 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-bold text-xs sm:text-base whitespace-nowrap">
                    Overview
                  </TabsTrigger>
                  <TabsTrigger value="experience" className="rounded-full px-4 sm:px-6 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-bold text-xs sm:text-base whitespace-nowrap">
                    Experience
                  </TabsTrigger>
                  <TabsTrigger value="interests" className="rounded-full px-4 sm:px-6 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-bold text-xs sm:text-base whitespace-nowrap">
                    Interests
                  </TabsTrigger>
                  <TabsTrigger value="countries" className="rounded-full px-4 sm:px-6 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-bold text-xs sm:text-base whitespace-nowrap">
                    Countries
                  </TabsTrigger>
                  {isOwnProfile && (
                    <TabsTrigger value="connections" className="rounded-full px-4 sm:px-6 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-bold text-xs sm:text-base whitespace-nowrap">
                      Connections
                    </TabsTrigger>
                  )}
                  {isOwnProfile && (
                    <TabsTrigger value="listings" className="rounded-full px-4 sm:px-6 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-bold text-xs sm:text-base whitespace-nowrap">
                      My Listings
                    </TabsTrigger>
                  )}
                  {isOwnProfile && (
                    <TabsTrigger value="security" className="rounded-full px-4 sm:px-6 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-bold text-xs sm:text-base whitespace-nowrap">
                      <Lock className="w-4 h-4 mr-1" />
                      Security
                    </TabsTrigger>
                  )}
                </TabsList>
              </div>

              {/* Overview Tab */}
              <TabsContent value="overview">
                <div className="bg-white rounded-3xl p-8 border-2 border-border">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-heading font-bold">Profile Information</h2>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-sm text-muted-foreground">Nationality</Label>
                      <p className="text-lg font-semibold">{profileData.nationality || 'Not specified'}</p>
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground">Birth Place</Label>
                      <p className="text-lg font-semibold">
                        {profileData.birth_city && profileData.birth_country 
                          ? `${profileData.birth_city}, ${profileData.birth_country}`
                          : 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground">Current Residence</Label>
                      <p className="text-lg font-semibold">
                        {profileData.residence_city}, {profileData.residence_country}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground">Member Since</Label>
                      <p className="text-lg font-semibold">
                        {new Date(profileData.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </p>
                    </div>
                  </div>

                  {/* Membership Status - Only visible on own profile */}
                  {isOwnProfile && !isIOSApp && (
                    <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Crown className="w-5 h-5 text-amber-500" />
                            <span className="font-bold text-amber-800">
                              {profileData.is_member 
                                ? (profileData.membership_expires && new Date(profileData.membership_expires) < new Date() 
                                    ? 'Membership Expired' 
                                    : 'Complimentary Member')
                                : 'Free Account'}
                            </span>
                          </div>
                          {profileData.is_member && profileData.membership_expires && (
                            <p className="text-sm text-amber-600">
                              {new Date(profileData.membership_expires) < new Date() 
                                ? 'Your complimentary membership expired on '
                                : 'Complimentary membership valid until '}
                              <span className="font-semibold">
                                {new Date(profileData.membership_expires).toLocaleDateString('en-US', { 
                                  month: 'long', 
                                  day: 'numeric', 
                                  year: 'numeric' 
                                })}
                              </span>
                            </p>
                          )}
                          {/* Renew button for expired membership */}
                          {isOwnProfile && profileData.is_member && profileData.membership_expires && new Date(profileData.membership_expires) < new Date() && (
                            <Link to="/membership">
                              <Button size="sm" className="mt-2 rounded-full bg-amber-600 hover:bg-amber-700" data-testid="renew-membership-btn">
                                Renew Membership
                              </Button>
                            </Link>
                          )}
                          {!profileData.is_member && (
                            <p className="text-sm text-amber-600">
                              Upgrade to access all features
                            </p>
                          )}
                        </div>
                        {profileData.is_member && (
                          <div className="bg-amber-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                            PRO
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {/* iOS PWA: Show upgrade prompt for non-members */}
                  {isOwnProfile && isIOSApp && !profileData.is_member && (
                    <div className="mt-6 p-4 rounded-xl bg-blue-50 border border-blue-200" data-testid="ios-upgrade-prompt">
                      <div className="flex items-center gap-2 mb-1">
                        <Crown className="w-5 h-5 text-blue-500" />
                        <span className="font-bold text-blue-800">Upgrade Available</span>
                      </div>
                      <p className="text-sm text-blue-600">
                        Visit <span className="font-semibold">expatssphere.com</span> in your browser to unlock all premium features.
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Experience Tab */}
              <TabsContent value="experience">
                <div className="bg-white rounded-3xl p-8 border-2 border-border">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-heading font-bold">Work Experience</h2>
                    {isEditing && (
                      <Button onClick={() => {}} className="bg-primary text-primary-foreground rounded-full">
                        Add Experience
                      </Button>
                    )}
                  </div>

                  <div className="space-y-6">
                    {profileData.work_experience && profileData.work_experience.length > 0 ? (
                      profileData.work_experience.map((work, index) => (
                        <div key={index} className="border-l-4 border-primary pl-6 py-2">
                          <h3 className="text-xl font-bold">{work.position}</h3>
                          <p className="text-lg text-muted-foreground">{work.company}</p>
                          <p className="text-sm text-muted-foreground">
                            {work.start_date} - {work.end_date || 'Present'} • {work.location}
                          </p>
                          {work.description && (
                            <p className="mt-2 text-muted-foreground">{work.description}</p>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                        <p className="text-muted-foreground">No work experience added yet</p>
                        {isOwnProfile && (
                          <p className="text-sm text-muted-foreground mt-2">Click "Edit Profile" to add your work experience</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Interests Tab */}
              <TabsContent value="interests">
                <div className="bg-white rounded-3xl p-8 border-2 border-border">
                  <h2 className="text-2xl font-heading font-bold mb-6">Interests</h2>
                  
                  {profileData.interests && profileData.interests.length > 0 ? (
                    <Accordion type="multiple" defaultValue={Object.keys(groupInterestsByCategory(profileData.interests))} className="w-full space-y-3">
                      {Object.entries(groupInterestsByCategory(profileData.interests)).map(([category, interests]) => {
                        const style = CATEGORY_STYLES[category];
                        
                        return (
                          <AccordionItem 
                            key={category} 
                            value={category}
                            className="border-0 rounded-xl overflow-hidden"
                          >
                            <AccordionTrigger 
                              className={`px-4 py-3 bg-gradient-to-r ${style.bgColor} text-white hover:no-underline rounded-xl data-[state=open]:rounded-b-none`}
                            >
                              <div className="flex items-center gap-3">
                                {category === "Business & Beyond" && <Briefcase className="w-5 h-5" />}
                                {category === "Fit & Fun" && <Dumbbell className="w-5 h-5" />}
                                {category === "Bites & Bright Lights" && <UtensilsCrossed className="w-5 h-5" />}
                                {category === "More to Explore" && <Compass className="w-5 h-5" />}
                                {category === "Arts & Culture" && <Palette className="w-5 h-5" />}
                                <span className="font-semibold">{category}</span>
                                <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                                  {interests.length}
                                </span>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="bg-gray-50 px-4 py-4 rounded-b-xl border-x border-b border-gray-200">
                              <div className="flex flex-wrap gap-2">
                                {interests.map((interest) => (
                                  <span
                                    key={interest}
                                    className={`px-3 py-1.5 rounded-full text-sm font-medium border ${style.tagColor}`}
                                  >
                                    {interest}
                                  </span>
                                ))}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        );
                      })}
                    </Accordion>
                  ) : (
                    <div className="text-center py-8 w-full">
                      <Heart className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                      <p className="text-muted-foreground">No interests added yet</p>
                      {isOwnProfile && (
                        <p className="text-sm text-muted-foreground mt-2">Click "Edit Profile" to add your interests</p>
                      )}
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Countries Tab */}
              <TabsContent value="countries">
                <div className="bg-white rounded-3xl p-8 border-2 border-border">
                  <h2 className="text-2xl font-heading font-bold mb-6">Countries Visited</h2>
                  <div className="flex flex-wrap gap-3">
                    {profileData.countries_visited && profileData.countries_visited.length > 0 ? (
                      profileData.countries_visited.map((country, index) => (
                        <span key={index} className="px-4 py-2 bg-secondary text-secondary-foreground rounded-full font-semibold">
                          {country}
                        </span>
                      ))
                    ) : (
                      <div className="text-center py-8 w-full">
                        <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                        <p className="text-muted-foreground">No countries added yet</p>
                        {isOwnProfile && (
                          <p className="text-sm text-muted-foreground mt-2">Click "Edit Profile" to add countries you've visited</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Connections Tab */}
              {isOwnProfile && (
                <TabsContent value="connections">
                  <div className="space-y-8">
                    {connections.pending_requests.length > 0 && (
                      <div className="bg-white rounded-3xl p-4 sm:p-8 border-2 border-border">
                        <h2 className="text-xl sm:text-2xl font-heading font-bold mb-4 sm:mb-6">Pending Requests ({connections.pending_requests.length})</h2>
                        <div className="space-y-4">
                          {connections.pending_requests.map((request) => (
                            <div key={request.user_id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-background rounded-xl">
                              <div className="flex items-center gap-3 sm:gap-4">
                                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary rounded-full flex items-center justify-center font-bold text-primary-foreground flex-shrink-0">
                                  {request.name.charAt(0)}
                                </div>
                                <div className="min-w-0">
                                  <p className="font-semibold truncate">{request.name}</p>
                                  <p className="text-sm text-muted-foreground truncate">{request.residence_city}, {request.residence_country}</p>
                                </div>
                              </div>
                              <div className="flex gap-2 ml-auto sm:ml-0 flex-shrink-0">
                                <Button onClick={() => handleAcceptConnection(request.user_id)} size="sm" className="bg-primary text-primary-foreground rounded-full px-4">
                                  Accept
                                </Button>
                                <Button onClick={() => handleRejectConnection(request.user_id)} size="sm" variant="outline" className="rounded-full px-4">
                                  Reject
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="bg-white rounded-3xl p-8 border-2 border-border">
                      <h2 className="text-2xl font-heading font-bold mb-6">My Connections ({connections.contacts.length})</h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {connections.contacts.length > 0 ? (
                          connections.contacts.map((contact) => (
                            <div key={contact.user_id} className="p-4 bg-background rounded-xl border-2 border-border hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/profile/${contact.user_id}`)}>
                              <div className="flex items-center gap-3 mb-2">
                                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center font-bold text-primary-foreground">
                                  {contact.name.charAt(0)}
                                </div>
                                <div>
                                  <p className="font-semibold">{contact.name}</p>
                                  <p className="text-sm text-muted-foreground">{contact.residence_city}</p>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="col-span-full text-center text-muted-foreground py-8">No connections yet</p>
                        )}
                      </div>
                    </div>

                    {/* Hidden Users Section */}
                    <div className="bg-white rounded-3xl p-8 border-2 border-border">
                      <div className="flex items-center gap-2 mb-6">
                        <EyeOff className="w-5 h-5 text-muted-foreground" />
                        <h2 className="text-2xl font-heading font-bold">Hidden Users ({dismissedUsers.length})</h2>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">
                        Users you've hidden won't appear in your "Expats to Connect With" recommendations. You can restore them anytime.
                      </p>
                      {loadingDismissed ? (
                        <div className="flex justify-center py-8">
                          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        </div>
                      ) : dismissedUsers.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {dismissedUsers.map((hiddenUser) => (
                            <div key={hiddenUser.user_id} className="p-4 bg-background rounded-xl border-2 border-border">
                              <div className="flex items-center justify-between">
                                <div 
                                  className="flex items-center gap-3 cursor-pointer hover:opacity-80"
                                  onClick={() => !hiddenUser.deleted && navigate(`/profile/${hiddenUser.user_id}`)}
                                >
                                  {hiddenUser.profile_picture ? (
                                    <img 
                                      src={hiddenUser.profile_picture} 
                                      alt={hiddenUser.name}
                                      className="w-10 h-10 rounded-full object-cover border-2 border-border"
                                    />
                                  ) : (
                                    <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center font-bold text-gray-600">
                                      {hiddenUser.name?.charAt(0) || '?'}
                                    </div>
                                  )}
                                  <div>
                                    <p className="font-semibold text-sm">{hiddenUser.name || 'Unknown'}</p>
                                    {hiddenUser.residence_city && (
                                      <p className="text-xs text-muted-foreground">{hiddenUser.residence_city}</p>
                                    )}
                                  </div>
                                </div>
                                <Button
                                  onClick={() => handleRestoreUser(hiddenUser.user_id)}
                                  size="sm"
                                  variant="outline"
                                  className="rounded-full text-xs px-3 hover:bg-green-50 hover:text-green-600 hover:border-green-600"
                                  data-testid={`restore-user-${hiddenUser.user_id}`}
                                >
                                  <Eye className="w-3 h-3 mr-1" />
                                  Restore
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-center text-muted-foreground py-8">No hidden users</p>
                      )}
                    </div>
                  </div>
                </TabsContent>
              )}

              {/* My Listings Tab */}
              {isOwnProfile && (
                <TabsContent value="listings">
                  <MyListingsTab />
                </TabsContent>
              )}

              {/* Security Tab */}
              {isOwnProfile && (
                <TabsContent value="security">
                  <div className="bg-white rounded-3xl p-8 border-2 border-border">
                    <h2 className="text-2xl font-heading font-bold mb-6">Security Settings</h2>
                    
                    {/* Biometric Login Section */}
                    <BiometricSettings />

                    {user?.is_admin && (
                      <>
                        <hr className="my-8 border-border" />
                        <div className="max-w-md mb-8">
                          <h3 className="text-lg font-semibold mb-2">Push Diagnostics</h3>
                          <p className="text-sm text-muted-foreground mb-4">
                            Inspect what the service worker observed for recent pushes on this device (admin only).
                          </p>
                          <Link to="/admin/push-diagnostics">
                            <Button
                              variant="outline"
                              className="rounded-full"
                              data-testid="push-diagnostics-profile-link"
                            >
                              Open Push Diagnostics
                            </Button>
                          </Link>
                        </div>
                      </>
                    )}

                    <hr className="my-8 border-border" />
                    
                    {/* Change Email Section */}
                    <div className="max-w-md mb-8">
                      <h3 className="text-lg font-semibold mb-2">Email Address</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Current email: <strong>{profileData?.email}</strong>
                      </p>
                      
                      {/* Pending email change banner */}
                      {profileData?.pending_email && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4" data-testid="pending-email-banner">
                          <p className="text-sm text-amber-800 font-medium mb-1">
                            Pending email change to: <strong>{profileData.pending_email}</strong>
                          </p>
                          <p className="text-xs text-amber-600 mb-3">
                            Check your inbox for the verification link. If you didn't receive it:
                          </p>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-100"
                            data-testid="resend-email-change-btn"
                            onClick={async () => {
                              try {
                                const { data } = await axios.post(`${API}/auth/resend-email-change-verification`);
                                toast.success(data.message);
                              } catch (error) {
                                toast.error(error.response?.data?.detail || 'Failed to resend verification email');
                              }
                            }}
                          >
                            Resend Verification Email
                          </Button>
                        </div>
                      )}

                      {!showEmailChange ? (
                        <Button
                          variant="outline"
                          onClick={() => setShowEmailChange(true)}
                          className="rounded-full"
                          data-testid="change-email-toggle-btn"
                        >
                          Change Email
                        </Button>
                      ) : (
                        <form onSubmit={handleChangeEmail} className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">New Email</label>
                            <input
                              type="email"
                              value={emailChangeForm.newEmail}
                              onChange={(e) => setEmailChangeForm(prev => ({ ...prev, newEmail: e.target.value }))}
                              className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary"
                              placeholder="Enter new email"
                              required
                              data-testid="new-email-input"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2">Current Password</label>
                            <input
                              type="password"
                              value={emailChangeForm.password}
                              onChange={(e) => setEmailChangeForm(prev => ({ ...prev, password: e.target.value }))}
                              className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary"
                              placeholder="Confirm your password"
                              required
                              data-testid="email-change-password-input"
                            />
                          </div>
                          <p className="text-xs text-muted-foreground">
                            A verification link will be sent to your new email. Your email won't change until you confirm it.
                          </p>
                          <div className="flex gap-3">
                            <Button
                              type="submit"
                              disabled={changingEmail}
                              className="rounded-full"
                              data-testid="change-email-submit-btn"
                            >
                              {changingEmail ? 'Sending...' : 'Send Verification'}
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={() => { setShowEmailChange(false); setEmailChangeForm({ newEmail: '', password: '' }); }}
                              className="rounded-full"
                            >
                              Cancel
                            </Button>
                          </div>
                        </form>
                      )}
                    </div>

                    <hr className="mb-8" />

                    {/* Change Password Section */}
                    <div className="max-w-md">
                      <h3 className="text-lg font-semibold mb-4">Change Password</h3>
                      <form onSubmit={handleChangePassword} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Current Password</label>
                          <div className="relative">
                            <input
                              type={showCurrentPassword ? "text" : "password"}
                              value={passwordForm.currentPassword}
                              onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                              className="w-full pl-4 pr-12 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary"
                              placeholder="Current password"
                              data-testid="current-password-input"
                            />
                            <button
                              type="button"
                              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            >
                              {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-2">New Password</label>
                          <div className="relative">
                            <input
                              type={showNewPassword ? "text" : "password"}
                              value={passwordForm.newPassword}
                              onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                              className="w-full pl-4 pr-12 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary"
                              placeholder="New password (min 6 chars)"
                              data-testid="new-password-input"
                            />
                            <button
                              type="button"
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            >
                              {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-2">Confirm New Password</label>
                          <input
                            type="password"
                            value={passwordForm.confirmPassword}
                            onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                            className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary"
                            placeholder="Confirm new password"
                            data-testid="confirm-password-input"
                          />
                        </div>
                        
                        <Button
                          type="submit"
                          disabled={changingPassword}
                          className="w-full rounded-full"
                          data-testid="change-password-btn"
                        >
                          {changingPassword ? 'Changing Password...' : 'Change Password'}
                        </Button>
                      </form>
                    </div>
                  </div>
                </TabsContent>
              )}
            </Tabs>
          </motion.div>
        </div>
      </div>

      {/* Hosted / Attended events list dialog (opened from the activity stat cards) */}
      <EventsListDialog
        open={eventsListDialog.open}
        onOpenChange={(open) => setEventsListDialog({ open, type: open ? eventsListDialog.type : null })}
        type={eventsListDialog.type}
        userId={viewUserId}
        userName={profileData?.name}
        isOwnProfile={isOwnProfile}
      />
    </div>
  );
};

export default EnhancedProfile;
